import type { EnemyDef } from './types';

const MESH = (file: string, scale?: number): { meshPath: string; meshScale?: number } => ({
  meshPath: `/assets/3d/enemies/${file}`,
  meshScale: scale,
});

// Enemy stats scale with vault tier. Drops are resolved tier-based in dungeonStore
// via rollTierLoot(dropTier) — per-enemy `drops` arrays are reserved for bosses later.
//
// corruptedDataReward scales exponentially per tier (5–10 → 300–500) so the Decrypter
// economy gates progression: low-tier farming barely fills a Scrap-Parser buffer,
// while Omega CD-yield is enough to push Quantum-Nodes to capacity in seconds.
//
// Hybrid Trash + Elite tuning (2026-04):
// - `swarmling` is the universal trash mob — 1-shot kill with any kit, used as
//   filler in every vault to give a horde feel. 4-6 per wave alongside elites.
// - All other non-boss mobs (T2-T4) had their HP doubled so they live ~10 s and
//   their HP-bar is actually readable mid-fight. XP/credits/CD bumped ×1.3 so
//   slower kills don't murder the XP/sec curve too much.
// - True bosses (nexus_apex, omega_herald) untouched — already feel right.
export const ENEMIES: Record<string, EnemyDef> = {
  // Universal trash — same stats every vault, dies in 1 player hit. Comes in
  // packs of 4-6 to make every wave feel like an army instead of a duel.
  swarmling: {
    id: 'swarmling', nameKey: 'swarmling',
    maxHp: 10, attack: 2, defense: 0,
    xpReward: 3, creditReward: 2, corruptedDataReward: 1,
    tier: 1, drops: [],
    ...MESH('Orc_Skull.gltf', 0.45),
  },

  // Tier 1 — starter vault, beatable with fresh kit
  security_bot_mk1: {
    id: 'security_bot_mk1', nameKey: 'security_bot_mk1',
    maxHp: 36, attack: 3, defense: 0,
    xpReward: 13, creditReward: 10, corruptedDataReward: 7,
    tier: 1, drops: [],
    ...MESH('GreenBlob.gltf', 1.0),
  },
  glitch_drone: {
    id: 'glitch_drone', nameKey: 'glitch_drone',
    maxHp: 28, attack: 5, defense: 0,
    xpReward: 16, creditReward: 13, corruptedDataReward: 10,
    tier: 1, drops: [],
    attackKind: 'ranged',
    ...MESH('Pigeon.gltf', 1.0),
  },

  // Tier 2
  patrol_drone: {
    id: 'patrol_drone', nameKey: 'patrol_drone',
    maxHp: 90, attack: 6, defense: 2,
    xpReward: 26, creditReward: 20, corruptedDataReward: 20,
    tier: 2, drops: [],
    ...MESH('Goleling.gltf', 0.7),
  },
  reaver_bot: {
    id: 'reaver_bot', nameKey: 'reaver_bot',
    maxHp: 110, attack: 8, defense: 2,
    xpReward: 33, creditReward: 24, corruptedDataReward: 26,
    tier: 2, drops: [],
    ...MESH('Ninja.gltf', 0.7),
  },

  // Tier 3
  elite_sentinel: {
    id: 'elite_sentinel', nameKey: 'elite_sentinel',
    maxHp: 260, attack: 14, defense: 6,
    xpReward: 60, creditReward: 45, corruptedDataReward: 52,
    tier: 3, drops: [],
    ...MESH('Orc.gltf', 0.55),
  },
  void_stalker: {
    id: 'void_stalker', nameKey: 'void_stalker',
    maxHp: 220, attack: 18, defense: 3,
    xpReward: 65, creditReward: 52, corruptedDataReward: 65,
    tier: 3, drops: [],
    attackKind: 'ranged',
    ...MESH('Ghost.gltf', 0.55),
  },

  // Tier 4 — mini-bosses, clearly bigger than the player
  // Endgame difficulty pass (2026-04): HP × 1.5 + DEF × 2.5 over the trash-hybrid
  // baseline. With ~155 ATK Standard at L35 these now take ~4-5 s to kill instead
  // of ~2 s; with poor gear (~50 ATK) they take ~10-12 s — the "feels like a
  // proper fight" range. Rewards bumped × 1.3 to keep XP/sec sane.
  bunker_warden: {
    id: 'bunker_warden', nameKey: 'bunker_warden',
    maxHp: 660, attack: 22, defense: 25,
    xpReward: 145, creditReward: 125, corruptedDataReward: 170,
    tier: 4, drops: [],
    ...MESH('Yeti.gltf', 0.75),
    attacks: [
      // Charge: 0.6 s red line warning, then 1.5× hit if player still on the line
      { kind: 'charge', telegraphMs: 600, cooldownMs: 5500, damageMult: 1.5, color: '#ff4d4d' },
    ],
  },
  reactor_titan: {
    id: 'reactor_titan', nameKey: 'reactor_titan',
    maxHp: 840, attack: 25, defense: 30,
    xpReward: 190, creditReward: 150, corruptedDataReward: 220,
    tier: 4, drops: [],
    attackKind: 'ranged',
    ...MESH('Demon.gltf', 0.75),
    attacks: [
      // AOE pulse: 0.8 s orange ring under titan, then 1.0× hit to anyone in 3 units
      { kind: 'aoe_pulse', telegraphMs: 800, cooldownMs: 6000, damageMult: 1.0, radius: 3.0, color: '#ff9a2b' },
    ],
  },

  // Tier 5 — true bosses, room-filling presence
  // Endgame difficulty pass: HP × 2 + DEF × 3. At L35 mid-T5 gear nexus_apex
  // now lives ~5 s (was 1.9 s); omega_herald ~7-8 s (was 2.75 s). Boss-wave
  // multiplier (×3 HP) on top makes the final boss a proper 20-25 s duel.
  // Only fully ascended Ancient kits melt these in <3 s — by design.
  nexus_apex: {
    id: 'nexus_apex', nameKey: 'nexus_apex',
    maxHp: 1000, attack: 35, defense: 55,
    xpReward: 260, creditReward: 235, corruptedDataReward: 390,
    tier: 5, drops: [],
    ...MESH('MushroomKing.gltf', 0.65),
    attacks: [
      // Chain lightning: hits player + 2 nearest swarmlings simultaneously
      { kind: 'chain', telegraphMs: 500, cooldownMs: 7000, damageMult: 0.7, color: '#9affc8' },
    ],
  },
  omega_herald: {
    id: 'omega_herald', nameKey: 'omega_herald',
    maxHp: 1400, attack: 45, defense: 65,
    xpReward: 390, creditReward: 390, corruptedDataReward: 585,
    tier: 5, drops: [],
    ...MESH('Dragon.gltf', 0.5),
    attacks: [
      // Big AOE pulse: 1.0 s magenta ring (5u), then 1.8× hit if caught inside
      { kind: 'aoe_pulse', telegraphMs: 1000, cooldownMs: 8000, damageMult: 1.8, radius: 5.0, color: '#ff4af0' },
    ],
  },
};

/** Boss-wave multiplier on `corruptedDataReward` — boss spawns drop 5× the base CD. */
export const BOSS_CD_MULTIPLIER = 5;

export function getEnemy(id: string): EnemyDef | undefined {
  return ENEMIES[id];
}
